// Shared fetch wrapper for the Laravel + Sanctum SPA backend.
//
// Responsibilities:
//   - Lazy-prime the CSRF cookie on the first mutation (Sanctum requires
//     a GET /sanctum/csrf-cookie before any state-changing request).
//   - Read the XSRF-TOKEN cookie and forward it as the X-XSRF-TOKEN header.
//   - Always send credentials so the session cookie + CSRF cookie travel.
//   - Throw a structured ApiError on non-2xx so callers can show validation
//     errors per-field without re-parsing the response body.
//
// In dev, both SPA (localhost:5173) and API (localhost) share the same host
// so cookies work without SESSION_DOMAIN config. In prod, atlasguess.com
// (SPA) and api.atlasguess.com (API) share via SESSION_DOMAIN=.atlasguess.com.

const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost';

let csrfPrimed = false;

/**
 * Force the next mutation to re-fetch the CSRF cookie. Call after logout —
 * the new session will have a fresh token that the cached one doesn't match.
 */
export function resetCsrf(): void {
  csrfPrimed = false;
}

async function primeCsrf(): Promise<void> {
  if (csrfPrimed) return;
  const res = await fetch(`${API_BASE}/sanctum/csrf-cookie`, { credentials: 'include' });
  if (!res.ok) {
    throw new ApiError(res.status, 'Failed to initialize session.', null);
  }
  csrfPrimed = true;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? match.slice(name.length + 1) : null;
}

export interface ValidationErrors {
  [field: string]: string[];
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly errors: ValidationErrors | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * First validation message for a given field, if any. Convenient for
   * `<Field error={err?.fieldError('email')} />` patterns.
   */
  fieldError(field: string): string | null {
    return this.errors?.[field]?.[0] ?? null;
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const isMutation = method !== 'GET' && method !== 'HEAD';

  if (isMutation) await primeCsrf();

  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');

  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Sanctum stateful guard reads X-XSRF-TOKEN; cookie value is URL-encoded.
  if (isMutation) {
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) headers.set('X-XSRF-TOKEN', decodeURIComponent(xsrf));
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (res.status === 204) {
    return undefined as T;
  }

  let body: unknown = null;
  const text = await res.text();
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      // Server returned non-JSON (e.g. an HTML error page from a misrouted
      // request). Fall through with body=null; the error path below still
      // surfaces a useful status code.
    }
  }

  if (!res.ok) {
    const message = isErrorBody(body) ? body.message : `Request failed with status ${res.status}`;
    const errors = isErrorBody(body) && typeof body.errors === 'object' ? (body.errors as ValidationErrors) : null;
    throw new ApiError(res.status, message, errors);
  }

  return body as T;
}

function isErrorBody(body: unknown): body is { message: string; errors?: unknown } {
  return typeof body === 'object' && body !== null && 'message' in body;
}
